import { useImpersonationStore } from '../stores/appImpersonationStore';
import { Link, matchPath, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../services/authentication/authContext';
import { getMainPageRestrictionRedirect } from '../services/accessManagement/mainPageRestriction';
import { getMenuChangeReturnPath } from '../services/menuChange/menuChangeLinks';
import { primaryText } from '../styling/stylingHelper';

// Public/auth recovery routes must remain usable when /me is unavailable. Keep
// this limited to AppRoutes' public routes; protected pages still wait for /me.
const PUBLIC_PATHS = [
  '/', '/login', '/signup', '/book-demo', '/forgot-password', '/reset-password',
  '/terms-and-conditions', '/privacy-policy', '/culinary-cloud-privacy-policy',
  '/cookies-policy', '/features/:slug',
];

export const isPublicAppPath = (pathname: string): boolean =>
  PUBLIC_PATHS.some(path => matchPath(path, pathname));

/** Resolve the profile before any protected page can mount, including invoice utilities. */
export function MainPageRestrictionRoute({ children }: { children: JSX.Element }) {
  const { user, backendUser, accountSettings, loading } = useAuth();
  const target = useImpersonationStore(state => state.target);
  const expectedUserId = target?.userId ?? user?.id;
  const { pathname } = useLocation();
  const { t } = useTranslation();
  if (loading || (user && (!backendUser || backendUser.id !== expectedUserId))) {
    if (isPublicAppPath(pathname)) return children;
    return <div className="p-6">{t('common.loading')}</div>;
  }
  if (!user) return children;
  if (backendUser?.role === 'staff' && ['pending', 'rejected'].includes(backendUser.access_request ?? '')) {
    return pathname === '/access' ? children : <Navigate to="/access" replace />;
  }
  const redirect = getMainPageRestrictionRedirect(pathname, backendUser, accountSettings);
  if (redirect && getMenuChangeReturnPath(pathname)) return <div className={`p-6 ${primaryText}`}><p role="alert">{t('menuChange.links.accessDenied')}</p><Link to={redirect} className="underline">{t('menuChange.links.back')}</Link></div>;
  return redirect ? <Navigate to={redirect} replace /> : children;
}
